import React from 'react';
import styled from 'styled-components';
import { UserCheck, Link as LinkIcon, Eye } from 'lucide-react';
import { useQuery } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const Tab = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border: none;
  background: none;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  font-weight: ${props => props.active ? 600 : 400};
  margin-bottom: -2px;
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const TableContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
`;

const Badge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.bg || props.theme.colors.background};
  color: ${props => props.color || props.theme.colors.text};
`;

const LoadingState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

function Referrals() {
  const [activeTab, setActiveTab] = React.useState('events');

  const { data: eventsData, isLoading: eventsLoading } = useQuery(
    'referral-events',
    () => adminAPI.getReferralEvents(),
    { enabled: activeTab === 'events' }
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      return '-';
    }
  };

  const getEventTypeBadge = (eventType) => {
    const configs = {
      start: { bg: '#dbeafe', color: '#2563eb', label: 'Начало' },
      signup: { bg: '#d1fae5', color: '#059669', label: 'Регистрация' },
      payment: { bg: '#fef3c7', color: '#d97706', label: 'Платеж' },
    };
    const config = configs[eventType] || { bg: '#f3f4f6', color: '#6b7280', label: eventType };
    return <Badge bg={config.bg} color={config.color}>{config.label}</Badge>;
  };

  return (
    <Container>
      <Header>
        <Title>
          <UserCheck size={32} />
          Рефералы
        </Title>
      </Header>

      <TabContainer>
        <Tab active={activeTab === 'events'} onClick={() => setActiveTab('events')}>
          События
        </Tab>
      </TabContainer>

      {eventsLoading ? (
        <LoadingState>Загрузка...</LoadingState>
      ) : !eventsData || !eventsData.data || !eventsData.data.events || eventsData.data.events.length === 0 ? (
        <EmptyState>Событий не найдено</EmptyState>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <Th>ID</Th>
                  <Th>Link ID</Th>
                  <Th>User ID</Th>
                  <Th>Тип</Th>
                  <Th>Сумма</Th>
                  <Th>Валюта</Th>
                  <Th>Дата</Th>
                </tr>
              </TableHeader>
              <tbody>
                {eventsData.data.events.map((event) => (
                  <tr key={event.id}>
                    <Td>{event.id}</Td>
                    <Td>{event.ref_link_id || '-'}</Td>
                    <Td>{event.user_id || (event.tg_id ? `TG:${event.tg_id}` : '-')}</Td>
                    <Td>{getEventTypeBadge(event.event)}</Td>
                    <Td>{event.amount != null ? `${event.amount}` : '-'}</Td>
                    <Td>{event.currency || '-'}</Td>
                    <Td>{formatDate(event.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <div style={{ marginTop: '16px', textAlign: 'center', color: '#6b7280' }}>
            Всего: {eventsData.data.total || 0}
          </div>
        </>
      )}
    </Container>
  );
}

export default Referrals;
